import { UlmInputLabel } from "@components/ulm/ulm-input-label";
import { TextField } from "@flat-ui";
import { Box } from "@mui-wrapper";
import { fieldSpacingSx } from "@styles/common-styles";
import { SignupBackButton } from "@components/signup/signup-back-button";
import { TrialProps } from "@pages/trial/trial-types";
import { useState } from "react";
import { SignupNewsletterCheckbox } from "@components/signup/signup-newsletter-checkbox";
import { UlmButton } from "@components/ulm/ulm-button";
import { useGetTrialType } from "@hooks/use-get-trial-type";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { faroCoreApiClient } from "@api/core-api/core-api-utils";
import { runtimeConfig } from "@src/runtime-config";
import { redirectClient } from "@utils/client-utils";
import { useAppSelector } from "@store/store-helper";
import { ulmConfigClientSelector } from "@store/ulm-config/ulm-config-selector";
import { useErrorHandlerContext } from "@context-providers/error-handler/error-handler-context";
import { SignupForm } from "@components/signup/signup-form/signup-form";
import { useSignupForm } from "@components/signup/signup-form/use-signup-form";

type Props = Pick<TrialProps, "setStep" | "email" | "authMethod">;

/**
 * Renders the third step of the trial registration flow.
 * Users can set a name for their workspace.
 * Additionally, If the user selected email/password authentication method
 * in the previous step they can pass their first name, last name and password.
 */
export function TrialStepThree({
  setStep,
  email,
  authMethod,
}: Props): JSX.Element {
  const { handleError } = useErrorHandlerContext();
  const { trialType } = useGetTrialType();
  const client = useAppSelector(ulmConfigClientSelector);
  const {
    form,
    setForm,
    errors,
    setErrors,
    setIsPasswordValid,
    validateForm: validateEmailForm,
  } = useSignupForm();

  const [workspaceName, setWorkspaceName] = useState<string>("");
  const [shouldSubscribeToNewsletter, setShouldSubscribeToNewsletter] =
    useState<boolean>(false);
  const [isWorkspaceNameError, setIsWorkspaceNameError] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  /** Validates the form inputs */
  function validateForm(): boolean {
    let isValid = true;
    let isEmailFormValid = true;

    if (workspaceName.trim() === "") {
      isValid = false;
      setIsWorkspaceNameError(true);
    }

    if (authMethod === "email") {
      isEmailFormValid = validateEmailForm();
    }

    return isValid && isEmailFormValid;
  }

  /** Constructs the payload for registering a trial account */
  function getTrialPayload(): FaroCoreApiTypes.RegisterTrialPayload {
    const payload: FaroCoreApiTypes.RegisterTrialPayload = {
      provider: authMethod,
      companyName: workspaceName.trim(),
      consents: shouldSubscribeToNewsletter
        ? ["newsletter", "promotional"]
        : [],
      trialType: trialType ?? undefined,
    };

    if (authMethod === "email") {
      payload.user = {
        email,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        password: form.password,
      };
    }

    return payload;
  }

  /** If the form is valid, registers the trial account and redirects to their new workspace */
  async function onClick(): Promise<void> {
    setIsLoading(true);

    const isFormValid = validateForm();

    if (isFormValid) {
      try {
        const { redirect } = await faroCoreApiClient.users.registerTrial({
          payload: getTrialPayload(),
          redirect: runtimeConfig.urls.sphereDashboardUrl,
        });
        redirectClient({
          client,
          redirectUrl: redirect,
        });
      } catch (error) {
        handleError({
          id: `registerTrial-${Date.now().toString()}`,
          title: "Failed to create trial account.",
          error,
          uiType: "toast",
        });
      }
    }

    setIsLoading(false);
  }

  function onChangeWorkspaceName(
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void {
    if (isWorkspaceNameError) {
      setIsWorkspaceNameError(false);
    }
    setWorkspaceName(event.target.value);
  }

  return (
    <Box
      data-testid="trial-step-three"
      sx={{
        width: "100%",
        marginTop: "16px",
      }}
    >
      <UlmInputLabel title="Workspace name" />
      <TextField
        fullWidth
        controlStyle={fieldSpacingSx}
        text={workspaceName}
        type="text"
        placeholder="For example, company’s or department’s name"
        error={isWorkspaceNameError ? "Workspace name is required" : undefined}
        InputProps={{ onChange: onChangeWorkspaceName }}
      />
      {authMethod === "email" && (
        <SignupForm
          form={form}
          setForm={setForm}
          errors={errors}
          setErrors={setErrors}
          setIsPasswordValid={setIsPasswordValid}
        />
      )}
      <SignupNewsletterCheckbox
        shouldSubscribeToNewsletter={shouldSubscribeToNewsletter}
        setShouldSubscribeToNewsletter={setShouldSubscribeToNewsletter}
      />
      <UlmButton
        onClick={onClick}
        isLoading={isLoading}
        sx={{
          marginY: "16px",
        }}
      >
        Get Started
      </UlmButton>
      <SignupBackButton
        handleBack={() => setStep("two")}
        isLoading={isLoading}
      />
    </Box>
  );
}
